import React from 'react';
import getConfig from 'next/config';
import { ApolloProvider } from '@apollo/client';
import { getDataFromTree } from '@apollo/client/react/ssr';

import SkipLink from '@domain-group/fe-co-skip-link';

import Header from '@domain-group/fe-co-header';
import { GetServerSideProps } from 'next';
import { skipLinkName } from '../../helper/accessibility-utils';
import { getDomainWinstonLogger } from '../../core/logger';
import { ErrorBoundary, SearchResults } from '../../components';
import { addApolloState, initializeApollo } from '../../lib/apollo';
import { mapHeaderData } from '../../components/header/utils';
import { AppPageProps } from '../_app';
import { UserDetailsRedacted } from '../../@types';
import { getUserDetails } from '../../utils';
import { ServerInfoContext } from '../../contexts';

const { publicRuntimeConfig } = getConfig();

interface AgentSearchResultsProps extends AppPageProps {
  isEmbeddedApp: boolean;
  user?: UserDetailsRedacted;
  suburbs: string;
  sortBy?: string;
  page?: string;
  keywordName?: string;
  agentFilter?: string;
  mixpanelHasInitialized?: boolean;
}

const AgentSearchResults = ({
  isEmbeddedApp,
  user,
  suburbs,
  sortBy,
  page = '1',
  keywordName,
  agentFilter,
  mixpanelHasInitialized = false,
}: AgentSearchResultsProps): JSX.Element => (
  <ErrorBoundary>
    <>
      <SkipLink anchorName={skipLinkName} />
      {isEmbeddedApp ? null : (
        <div data-theme-name="domain-2022">
          <Header
            {...mapHeaderData({
              baseUrl: publicRuntimeConfig.BASE_URL,
              breadcrumbsData: null,
            })}
          />
        </div>
      )}
      <SearchResults
        isEmbeddedApp={isEmbeddedApp}
        isAgency={false}
        suburbs={suburbs}
        sortBy={sortBy}
        page={page}
        keywordName={keywordName}
        agentFilter={agentFilter}
        skipLinkContentId={skipLinkName}
        user={user}
        mixpanelHasInitialized={mixpanelHasInitialized}
      />
    </>
  </ErrorBoundary>
);

export default AgentSearchResults;

export const getServerSideProps: GetServerSideProps<
  AgentSearchResultsProps
> = async ({ req, res, query }) => {
  const logger = getDomainWinstonLogger(__filename);
  const user = getUserDetails(req.user);

  let suburbs;
  if (typeof query.searchParam === 'string') {
    suburbs = query.searchParam;
  } else if (typeof query.suburbs === 'string') {
    suburbs = query.suburbs;
  } else {
    // No suburbs passed - redirect to landing page
    return {
      redirect: {
        destination: '/real-estate-agents',
      },
    };
  }

  const props = {
    // _app.js props
    serverInfo: res.locals.serverInfo,
    mixpanelToken: publicRuntimeConfig.MIXPANEL_TOKEN,

    isEmbeddedApp: req.isEmbeddedApp,

    // User data
    user,

    // page props
    suburbs,
    sortBy: typeof query.sortBy === 'string' ? query.sortBy : undefined,
    keywordName:
      typeof query.keywordName === 'string' ? query.keywordName : undefined,
    page: typeof query.page === 'string' ? query.page : undefined,
    agentFilter:
      typeof query.agentFilter === 'string' ? query.agentFilter : undefined,
  };

  // Remove "undefined" keys
  const pageProps: { props: AgentSearchResultsProps } = JSON.parse(
    JSON.stringify({
      props,
    }),
  );

  const apolloClient = initializeApollo();

  try {
    await getDataFromTree(
      <ServerInfoContext.Provider value={pageProps.props.serverInfo}>
        <ApolloProvider client={apolloClient}>
          <AgentSearchResults {...pageProps.props} />
        </ApolloProvider>
      </ServerInfoContext.Provider>,
    );

    return addApolloState(apolloClient, pageProps);
  } catch (error: any) {
    logger.error('GraphQL SSR error\n', error);

    // Handle 404s
    if (
      // https://github.com/domain-group/fe-pa-trade-profile/blob/master/src/js/agency-profile.js#L77
      error?.message?.includes?.('Insufficient data')
    ) {
      return {
        notFound: true,
      };
    }

    throw new Error(error);
  }
};
